import { css, cx } from '@repo/pandacss/styled-system/css';
import { type CSSProperties } from 'react';

type SkeletonProps = {
  width?: number | string;
  height?: number | string;
  className?: string;
  variant?: 'rounded' | 'circular' | 'text' | 'rectangular';
  animation?: boolean;
};

type VariantDefaults = { borderRadius: string; height?: string };

const defaultsByVariant: Record<NonNullable<SkeletonProps['variant']>, VariantDefaults> = {
  circular: { borderRadius: '9999px' },
  rounded: { borderRadius: '4px' },
  text: { borderRadius: '4px', height: '1rem' },
  rectangular: { borderRadius: '0' },
};

const animationClassName = css({
  animationName: '[pulse]',
  animationDuration: '[1.5s]',
  animationTimingFunction: '[ease-in-out]',
  animationIterationCount: '[infinite]',
});

const toPx = (value: number | string) => (typeof value === 'number' ? `${value}px` : value);

export const Skeleton = ({
  width,
  height,
  className,
  variant = 'rounded',
  animation = true,
}: SkeletonProps) => {
  const defaults = defaultsByVariant[variant];
  return (
    <span
      className={cx(
        css({
          display: 'block',
          backgroundColor: 'neutral.lighter',
          borderTopLeftRadius: 'var(--skeleton-border-radius)',
          borderTopRightRadius: 'var(--skeleton-border-radius)',
          borderBottomRightRadius: 'var(--skeleton-border-radius)',
          borderBottomLeftRadius: 'var(--skeleton-border-radius)',
        }),
        animation ? animationClassName : undefined,
        className,
      )}
      style={
        {
          '--skeleton-border-radius': defaults.borderRadius,
          width: width !== undefined ? toPx(width) : undefined,
          height: height !== undefined ? toPx(height) : defaults.height,
        } as CSSProperties
      }
    />
  );
};
